import React, { useState } from "react";
import { HiOutlinePlus, HiOutlineInformationCircle } from "react-icons/hi";
import { toast } from "react-hot-toast";
import { useHistory, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import API from "../../api";
import Trigger from "../../components/question/Trigger";
import DependencyExplorer from "../../components/dependencies/DependencyExplorer";
import RenditionOptions from "../../components/question/RenditionOptions";
import { validateTriggerGroups, INPUT_CLASS_NAME, newObjectId } from "../../utils";
import { capture } from "../../instrument";
import { LuGitBranch } from "react-icons/lu";
import { HiOutlineX } from "react-icons/hi";

const AUDIENCES = [
  { audience: "secretary", label: "Secrétaire", dotClass: "bg-purple-500", isCanonical: true },
  { audience: "patient", label: "Patient", dotClass: "bg-blue-500" },
  { audience: "ide", label: "IDE", dotClass: "bg-green-500" },
];

const QUESTION_TEXTAREA_CLASS = "w-full resize-y rounded-md border border-gray-200 bg-white px-3 py-2 text-sm leading-snug text-gray-900 placeholder:text-gray-300 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500";

const AddQuestion = () => {
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();
  const duplicate = location.state?.duplicate;

  const [question, setQuestion] = useState(duplicate?.question || "");
  const [isRequired, setIsRequired] = useState(duplicate?.isRequired !== false);
  const [patient, setPatient] = useState(duplicate?.patient ?? false);
  const [patientQuestion, setPatientQuestion] = useState(duplicate?.patientQuestion || "");
  const [patientIsRequired, setPatientIsRequired] = useState(duplicate?.patientIsRequired !== false);
  const [ide, setIde] = useState(duplicate?.ide ?? false);
  const [ideQuestion, setIdeQuestion] = useState(duplicate?.ideQuestion || "");
  const [ideIsRequired, setIdeIsRequired] = useState(duplicate?.ideIsRequired !== false);
  const [canAnswerInPreRegistration, setCanAnswerInPreRegistration] = useState(duplicate?.canAnswerInPreRegistration ?? false);
  const [questionCode, setQuestionCode] = useState(duplicate?.questionCode || "");
  const [answerType, setAnswerType] = useState(duplicate?.answerType || "MULTIPLE_CHOICE");
  const [groupsOperator, setGroupsOperator] = useState(duplicate?.groupsOperator || "AND");
  const [triggerGroups, setTriggerGroups] = useState(duplicate?.triggerGroups || [{ operator: "AND", triggers: [{ type: "CODE_SOME", values: [], field: "" }] }]);
  const [options, setOptions] = useState(
    (duplicate?.options || []).map((o) => {
      const p = (duplicate?.patientOptions || []).find((x) => x.optionId?.toString() === o._id?.toString());
      const i = (duplicate?.ideOptions || []).find((x) => x.optionId?.toString() === o._id?.toString());
      return { _id: o._id, code: o.code, label: o.label || "", patientLabel: p?.label || "", patientHidden: !!p?.hidden, ideLabel: i?.label || "", ideHidden: !!i?.hidden };
    }),
  );
  const [skipIfAlreadyAnswered, setSkipIfAlreadyAnswered] = useState(duplicate?.skipIfAlreadyAnswered ?? false);
  const [autoAnswer, setAutoAnswer] = useState(duplicate?.autoAnswer ?? false);
  const diagramOpen = true;

  const { data: diagramData } = useQuery({
    queryKey: ["diagramData"],
    queryFn: async () => {
      const [questions, guidelines, actions] = await Promise.all([API.get("/question"), API.get("/guideline"), API.get("/action")]);
      if (!questions.ok || !guidelines.ok || !actions.ok) throw new Error("Impossible de charger les dépendances");
      return { questions: questions.data, guidelines: guidelines.data, actions: actions.data };
    },
    enabled: diagramOpen,
  });

  const createMutation = useMutation({
    mutationFn: (body) => API.post("/question", body),
    onSuccess: (response) => {
      if (!response.ok) return toast.error(response.message);
      toast.success("Question ajoutée avec succès");
      queryClient.invalidateQueries({ queryKey: ["questions"] });
      queryClient.invalidateQueries({ queryKey: ["diagramData"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["taskCount"] });
      history.push("/question");
    },
    onError: (error) => {
      capture(error);
      toast.error("Une erreur est survenue");
    },
  });

  const hasOptions = answerType === "MULTIPLE_CHOICE" || answerType === "TEXT";
  const audienceOptionOverrides = (labelField, hiddenField) =>
    hasOptions
      ? options.flatMap((o) => {
          const label = o[labelField] && o[labelField] !== o.label ? o[labelField] : "";
          return label || o[hiddenField] ? [{ optionId: o._id, label, hidden: !!o[hiddenField] }] : [];
        })
      : [];

  const addQuestion = () => {
    if (!question || !answerType || !questionCode || !triggerGroups?.length) return toast.error("Veuillez remplir tous les champs");

    const error = validateTriggerGroups(triggerGroups);
    if (error) return toast.error(error);

    createMutation.mutate({
      question,
      isRequired,
      patient,
      patientQuestion: patient && patientQuestion && patientQuestion !== question ? patientQuestion : "",
      patientOptions: patient ? audienceOptionOverrides("patientLabel", "patientHidden") : [],
      patientIsRequired,
      ide,
      ideQuestion: ide && ideQuestion && ideQuestion !== question ? ideQuestion : "",
      ideOptions: ide ? audienceOptionOverrides("ideLabel", "ideHidden") : [],
      ideIsRequired,
      canAnswerInPreRegistration,
      questionCode,
      answerType,
      triggers: [],
      groupsOperator,
      triggerGroups,
      options: hasOptions ? options.map(({ _id, code, label }) => ({ _id, code, label })) : [],
      skipIfAlreadyAnswered,
      autoAnswer,
    });
  };

  const onAddOption = () => {
    setOptions((prev) => [...prev, { _id: newObjectId(), code: "", label: "", patientLabel: "", patientHidden: false, ideLabel: "", ideHidden: false }]);
  };

  const onRemoveOption = (optionId) => {
    setOptions((prev) => prev.filter((o) => o._id !== optionId));
  };

  const onChangeOptionCode = (optionId, code) =>
    setOptions((prev) => prev.map((o) => o._id === optionId ? { ...o, code } : o));

  const onChangeOptionLabel = (audience, optionId, value) => {
    const field = audience === "secretary" ? "label" : audience === "patient" ? "patientLabel" : "ideLabel";
    setOptions((prev) => prev.map((o) => o._id === optionId ? { ...o, [field]: value } : o));
  };

  const onToggleOptionDisabled = (audience, optionId) => {
    const field = audience === "patient" ? "patientHidden" : "ideHidden";
    setOptions((prev) => prev.map((o) => o._id === optionId ? { ...o, [field]: !o[field] } : o));
  };

  const onReorderOptions = (orderedOptionIds) => {
    setOptions((prev) => orderedOptionIds.map((id) => prev.find((o) => o._id?.toString() === id?.toString())).filter(Boolean));
  };

  const onChangeAudienceRequired = (audience, value) => {
    if (audience === "secretary") setIsRequired(value);
    else if (audience === "patient") setPatientIsRequired(value);
    else setIdeIsRequired(value);
  };

  return (
    <div className="flex h-[calc(100vh-60px)] flex-col bg-gray-50">
      <div className="flex items-start justify-between border-b border-gray-200 bg-white px-8 pb-5 pt-6">
        <div className="min-w-0">
          <h1 className="text-lg font-semibold text-gray-900">{duplicate ? "Dupliquer une question" : "Ajouter une question"}</h1>
        </div>
        <div className="flex shrink-0 items-center gap-1.5 pl-4">
          <button
            type="button"
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2"
            onClick={() => history.push("/question")}
          >
            Annuler
          </button>
          <button
            type="button"
            className="rounded-lg bg-primary-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
            onClick={addQuestion}
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? "Chargement..." : "Ajouter"}
          </button>
        </div>
      </div>

      <div className="grid flex-1 grid-cols-5 overflow-hidden">
        <div className="col-span-3 overflow-y-auto">
          <div className="mx-auto max-w-4xl px-8 py-6">
            <h3 className="mb-4 text-lg font-medium text-gray-900">Général</h3>

            <div className="divide-y divide-gray-100 rounded-lg border border-gray-100 bg-white">
              <div className="flex items-center gap-4 px-5 py-3.5">
                <label className="w-44 shrink-0 text-[13px] font-medium text-gray-600">Type de réponse</label>
                <select className={INPUT_CLASS_NAME} value={answerType} onChange={(e) => setAnswerType(e.target.value)}>
                  <option value="MULTIPLE_CHOICE">Choix multiple</option>
                  <option value="TEXT">Texte</option>
                  <option value="DATE">Date</option>
                  <option value="HOUR">Heure</option>
                  <option value="DOCUMENT">Document</option>
                </select>
              </div>
              <div className="flex items-center gap-4 px-5 py-3.5">
                <label className="w-44 shrink-0 text-[13px] font-medium text-gray-600">{answerType === "DOCUMENT" ? "Nom du document" : "Code de la question"}</label>
                <input
                  className={INPUT_CLASS_NAME}
                  placeholder={answerType === "DOCUMENT" ? "Nom du document" : "Code unique pour cette question"}
                  value={questionCode}
                  onChange={(e) => setQuestionCode(e.target.value)}
                />
              </div>
            </div>

            <div className="mt-6 space-y-3">
              <div className="flex items-baseline justify-between px-1">
                <h4 className="text-sm font-medium text-gray-700">Versions par audience</h4>
                <p className="text-xs text-gray-400">Code partagé · libellés reformulés par audience</p>
              </div>

              {AUDIENCES.map(({ audience, label, dotClass, isCanonical }) => {
                if (!isCanonical && !(audience === "patient" ? patient : ide)) return null;
                const audienceQuestion = isCanonical ? question : audience === "patient" ? patientQuestion : ideQuestion;
                const audienceRequired = isCanonical ? isRequired : audience === "patient" ? patientIsRequired : ideIsRequired;
                return (
                  <div key={audience} className="overflow-hidden rounded-lg border border-gray-100 bg-white">
                    <div className="flex flex-wrap items-center gap-2.5 border-b border-gray-100 bg-gray-50/40 px-5 py-2.5">
                      <span className={`h-2 w-2 shrink-0 rounded-full ${dotClass}`} />
                      <span className="text-[13px] font-medium text-gray-800">{label}</span>
                      {isCanonical && (
                        <span className="rounded bg-white px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-500 ring-1 ring-gray-200">
                          canonique
                        </span>
                      )}
                      <label className="ml-2 flex cursor-pointer items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700">
                        <input
                          type="checkbox"
                          className="h-3.5 w-3.5 cursor-pointer rounded border-gray-300 text-primary-500 focus:ring-1 focus:ring-primary-500"
                          checked={audienceRequired !== false}
                          onChange={(e) => onChangeAudienceRequired(audience, e.target.checked)}
                        />
                        Obligatoire
                      </label>
                      {audience === "patient" && (
                        <label className="flex cursor-pointer items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700">
                          <input
                            type="checkbox"
                            className="h-3.5 w-3.5 cursor-pointer rounded border-gray-300 text-primary-500 focus:ring-1 focus:ring-primary-500"
                            checked={canAnswerInPreRegistration}
                            onChange={(e) => setCanAnswerInPreRegistration(e.target.checked)}
                          />
                          Réponse en pré-enregistrement
                        </label>
                      )}
                      {!isCanonical && (
                        <button
                          type="button"
                          onClick={() => {
                            if (audience === "patient") {
                              setPatient(false);
                              setPatientQuestion("");
                              setPatientIsRequired(true);
                              setOptions((prev) => prev.map((o) => ({ ...o, patientLabel: "", patientHidden: false })));
                              setCanAnswerInPreRegistration(false);
                            } else {
                              setIde(false);
                              setIdeQuestion("");
                              setIdeIsRequired(true);
                              setOptions((prev) => prev.map((o) => ({ ...o, ideLabel: "", ideHidden: false })));
                            }
                          }}
                          className="ml-auto rounded p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500"
                          title={`Supprimer la version ${label}`}
                        >
                          <HiOutlineX className="h-4 w-4" />
                        </button>
                      )}
                    </div>

                    <div className="space-y-4 px-5 py-4">
                      <div>
                        <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-gray-400">Énoncé</label>
                        <textarea
                          rows={2}
                          className={QUESTION_TEXTAREA_CLASS}
                          placeholder={isCanonical ? "Texte de la question" : question || "Texte de la question"}
                          value={audienceQuestion || ""}
                          onChange={(e) => {
                            if (isCanonical) setQuestion(e.target.value);
                            else if (audience === "patient") setPatientQuestion(e.target.value);
                            else setIdeQuestion(e.target.value);
                          }}
                        />
                      </div>

                      {hasOptions && (
                        <RenditionOptions
                          isCanonical={isCanonical}
                          answerType={answerType}
                          options={options}
                          optionLabels={options.map((o) => ({
                            optionId: o._id,
                            value: isCanonical ? o.label : audience === "patient" ? o.patientLabel : o.ideLabel,
                            disabled: !isCanonical && (audience === "patient" ? o.patientHidden : o.ideHidden),
                          }))}
                          secretaryOptionLabels={options.map((o) => ({ optionId: o._id, value: o.label }))}
                          onReorder={onReorderOptions}
                          onAddOption={onAddOption}
                          onRemoveOption={onRemoveOption}
                          onChangeOptionCode={onChangeOptionCode}
                          onChangeOptionLabel={(optionId, value) => onChangeOptionLabel(audience, optionId, value)}
                          onToggleOptionDisabled={(optionId) => onToggleOptionDisabled(audience, optionId)}
                        />
                      )}
                    </div>
                  </div>
                );
              })}

              {(!patient || !ide) && (
                <div className="flex flex-wrap items-center gap-2 px-1 pt-1">
                  <span className="text-xs text-gray-400">Ajouter une version :</span>
                  {AUDIENCES.filter(({ audience }) => (audience === "patient" && !patient) || (audience === "ide" && !ide)).map(({ audience, label, dotClass }) => (
                    <button
                      key={audience}
                      type="button"
                      onClick={() => {
                        if (audience === "patient") {
                          setPatientIsRequired(isRequired);
                          setPatient(true);
                        } else {
                          setIdeIsRequired(isRequired);
                          setIde(true);
                        }
                      }}
                      className="flex items-center gap-1.5 rounded-md border border-dashed border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:border-primary-400 hover:bg-primary-50 hover:text-primary-700"
                    >
                      <span className={`h-1.5 w-1.5 rounded-full ${dotClass}`} />
                      <HiOutlinePlus className="h-3.5 w-3.5" />
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-6 rounded-lg border border-gray-100 bg-white p-5">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="skipIfAlreadyAnswered"
                  className="h-5 w-5 cursor-pointer rounded border-gray-300 text-primary-500 focus:ring-2 focus:ring-primary-500"
                  checked={skipIfAlreadyAnswered}
                  onChange={(e) => setSkipIfAlreadyAnswered(e.target.checked)}
                />
                <label htmlFor="skipIfAlreadyAnswered" className="ml-3 cursor-pointer text-sm font-medium text-gray-700">
                  Ne pas poser si déjà répondu dans une admission précédente
                </label>
              </div>
              <p className="ml-8 mt-2 text-xs text-gray-500">
                Si cette case est cochée, cette question ne sera pas posée si le patient y a déjà répondu dans une admission précédente.
              </p>
              {(answerType === "DATE" || answerType === "HOUR") && (
                <>
                  <div className="mt-4 flex items-center">
                    <input
                      type="checkbox"
                      id="autoAnswer"
                      className="h-5 w-5 cursor-pointer rounded border-gray-300 text-primary-500 focus:ring-2 focus:ring-primary-500"
                      checked={autoAnswer}
                      onChange={(e) => setAutoAnswer(e.target.checked)}
                    />
                    <label htmlFor="autoAnswer" className="ml-3 cursor-pointer text-sm font-medium text-gray-700">
                      Réponse automatique
                    </label>
                  </div>
                  <p className="ml-8 mt-2 text-xs text-gray-500">
                    Si cette case est cochée, la réponse sera automatiquement remplie avec {answerType === "DATE" ? "la date" : "l'heure"} actuelle lors de l'envoi en production de
                    l'admission.
                  </p>
                </>
              )}
            </div>


            {answerType === "DOCUMENT" && (
              <div className="mt-6 flex gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                <HiOutlineInformationCircle className="mt-0.5 h-5 w-5 shrink-0 text-gray-400" />
                <p className="text-xs leading-relaxed text-gray-600">
                  Une fois le document fourni, cette question reste active même si ses conditions de déclenchement ne sont plus remplies. Ainsi le document (par exemple la photo de la
                  mutuelle) ne disparaît pas lorsque la secrétaire saisit les informations qu'il contient : il reste disponible pour être intégré au SIL. Pour le retirer, supprimez le
                  document depuis l'admission.
                </p>
              </div>
            )}

            <div className="mt-6 rounded-lg border border-gray-100 bg-white p-5">
              <Trigger groupsOperator={groupsOperator} setGroupsOperator={setGroupsOperator} triggerGroups={triggerGroups} setTriggerGroups={setTriggerGroups} codesIncludeRelated />
            </div>
          </div>
        </div>

        <div className="col-span-2 shrink-0 overflow-y-auto border-l border-gray-200 bg-white p-6">
          <div className="mb-4 flex items-center gap-2">
            <LuGitBranch className="h-5 w-5 text-primary-500" />
            <span className="text-sm font-medium text-gray-700">Dépendances</span>
          </div>
          {diagramData ? (
            <DependencyExplorer
              data={{
                questions: [
                  ...diagramData.questions,
                  {
                    _id: "new_question",
                    question,
                    isRequired,
                    patient,
                    ide,
                    canAnswerInPreRegistration,
                    questionCode,
                    options,
                    triggerGroups,
                    groupsOperator,
                  },
                ],
                guidelines: diagramData.guidelines,
                actions: diagramData.actions,
              }}
              currentQuestionId="new_question"
              className="rounded-lg border border-gray-100"
            />
          ) : (
            <div className="flex items-center justify-center rounded-lg border border-gray-100 py-8 text-sm text-gray-400">Chargement du diagramme...</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddQuestion;
